import * as React from 'react';
import moment from 'moment-timezone';
import { fetchTimezoneAbbreviation } from '../../../common/util';

/**
 * The custom-field values captured on a note.
 *
 * These used to be fetched per note from /cvncf when the row rendered. They now
 * arrive on the entry, already resolved against the note's template, so the
 * label and value are both present without a second lookup.
 */
const hasValue = (field) => !!(field.value && field.value.trim() !== "");

/** Standard fields every note template carries that the timeline never shows. */
const HIDDEN_FIELD_LABELS = new Set(["Summary", "Attach Files", "Attach Workkard Files"]);

/** e.g. "depositiondate_datetime_1af420a6" - the field's role, not its group. */
const isDatetimeField = (field) => (field.field_key || field.field_name || "").includes("_datetime_");
const isTimezoneField = (field) => (field.field_key || field.field_name || "").includes("_timezone_");

/**
 * fetchTimezoneAbbreviation only maps a *display string* ("Eastern Time (ET)")
 * to its abbreviation - it doesn't know the IANA zone the field actually holds
 * ("America/New_York"). tzData is the same lookup list noteDataTemplate.jsx
 * uses to bridge the two, keyed by UniversalTimeZoneId.
 */
function zoneAbbreviation(zone, tzData) {
    const entry = (tzData || []).find((tz) => tz.UniversalTimeZoneId === zone);
    return entry ? fetchTimezoneAbbreviation(entry.DisplayText) : moment.tz(zone).format("z");
}

function formatDateTimeGroup(datetimeField, timezoneField, tzData) {
    const seconds = Number(datetimeField.value);
    if (!Number.isFinite(seconds)) return datetimeField.value;
    const zone = timezoneField && timezoneField.value;
    const m = zone ? moment.unix(seconds).tz(zone) : moment.unix(seconds);
    const formatted = m.format("M/D/YYYY, h:mm A");
    const abbreviation = zone ? zoneAbbreviation(zone, tzData) : "";
    return abbreviation ? `${formatted} (${formatted} ${abbreviation})` : formatted;
}

function groupFields(fields) {
    const order = [];
    const groups = new Map();
    fields.forEach((field) => {
        const key = field.field_group_name || field.label;
        if (!groups.has(key)) {
            groups.set(key, []);
            order.push(key);
        }
        groups.get(key).push(field);
    });
    return order
        .map((key) => groups.get(key).sort((a, b) => Number(a.sequence) - Number(b.sequence)))
        .map((group) => ({ groupLabel: group[0].field_group_name || group[0].label, fields: group }))
}

function GroupedFieldValue({ group, tzData }) {
    const datetimeField = group.fields.find(isDatetimeField);
    const timezoneField = group.fields.find(isTimezoneField);
    if (datetimeField && hasValue(datetimeField)) {
        return <span className="mr">{formatDateTimeGroup(datetimeField, timezoneField, tzData)}</span>;
    }
    return (
        <>
            {group.fields.filter(hasValue).map((field) => (
                <span className="mr" key={field.field_key || field.custom_field_id}>{field.value}</span>
            ))}
        </>
    );
}

export default function NoteCustomFields({ fields, noteId, tzData }) {
    const visibleFields = (fields || []).filter((field) => !HIDDEN_FIELD_LABELS.has(field.label));
    const groups = groupFields(visibleFields);
    if (groups.length === 0) {
        return null;
    }
    return (
        <div className="custom_field_listing" id={`cf_con_${noteId}`}>
            <div className={groups.length <= 2 ? "getNotes col-2" : "getNotes col-3"}>
                <ul className="mb flexGrow">
                    {groups.map((group) => (
                        <li className="fl" key={group.groupLabel}>
                            {group.fields.length > 1 ? (
                                <>
                                    <label>{group.groupLabel}</label>
                                    <span><GroupedFieldValue group={group} tzData={tzData} /></span>
                                </>
                            ) : (
                                <span>
                                    <label>{group.fields[0].label}</label>
                                    <span>{group.fields[0].value}</span>
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
